<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation, useQuery, keepPreviousData } from '@tanstack/vue-query';
import * as yup from 'yup';
import { caregiversApi, type CaregiverFilter } from '@/api/caregivers';
import { patientsApi, type PatientFilter } from '@/api/patients';
import {
  shiftsApi, type CaregiverPricing, type CaregiverShift, type CustomShiftRange, type ShiftBatchCreateForm,
} from '@/api/shifts';
import type { AxiosError } from 'axios';
import { type SerializedUser } from '@/api/users';
import PriceInput from '@/components/price-input.vue';
import TimeInput from '@/components/time-input.vue';
import ComplianceCheckModal from '@/components/shifts/compliance-check-modal.vue';
import ShiftConflictDetector from '@/components/shifts/shift-conflict-detector.vue';
import ShiftDaysSelectionForm from '@/components/shifts/shift-days-selection-form.vue';
import { usePricingStructures } from '@/composables/usePricingStructures';
import { EMPTY_PRICING, type TransformedPricingStructure } from '@/composables/usePricingStructure';
import { usePermissions } from '@/composables/usePermissions';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import { useGroupFilterStore } from '@/stores/group-filter';
import ShiftsInfoBanner from '@/components/shifts/shifts-info-banner.vue';
import { useTableFilter } from '@/composables/useTableFilter';
import { getCareerColor } from '@/utils/careers';
import { requestErrorMessage } from '@/utils/api-errors';
import { memberWithoutGroups, resolveMemberGroupIds } from '@/utils/group-scope';
import { addMinutesToTime, endTimeFollowingStart } from '@/utils/time-of-day';

const nameFilter = useTableFilter(['fullName']);

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { hasPermission } = usePermissions();
const { trackShiftEvent } = useAnalytics();

interface Props {
  patient?: PatientFilter;
  source?: 'calendar' | 'patient_profile';
}

const props = defineProps<Props>();

const { t } = useI18n();

const canReadFinancialInfo = computed(() => hasPermission.value('shifts', 'read_financial_information'));
const chargesAndPaysPerHours = computed(() => currentUser?.value.companyChargesAndPaysPerHours ?? false);

const financialNumberField = canReadFinancialInfo.value ?
  yup.number().required(t('shiftBatchCreateForm.fieldRequired'))
    .typeError(t('shiftBatchCreateForm.fieldRequired')) : yup.number();
const withTaxBillField = canReadFinancialInfo.value ?
  yup.boolean().required(t('shiftForm.fieldRequired')) : yup.boolean();

const fixedRatePricingSchema = {
  caregiverWeekShiftCost: financialNumberField,
  caregiverWeekendShiftCost: financialNumberField,
  weekShiftPrice: financialNumberField,
  weekendShiftPrice: financialNumberField,
};

const perHourPricingSchema = {
  caregiverWeekShiftCostPerHour: financialNumberField,
  caregiverWeekendShiftCostPerHour: financialNumberField,
  weekShiftPricePerHour: financialNumberField,
  weekendShiftPricePerHour: financialNumberField,
};

// One pricing entry per selected caregiver; the backend prices each caregiver
// from its own entry (see CreateShiftsJob), so nothing is shared across them.
const caregiverPricingSchema = yup.object({
  caregiverId: yup.number().required(),
  withTaxBill: withTaxBillField,
  ...(chargesAndPaysPerHours.value ? perHourPricingSchema : fixedRatePricingSchema),
});

const validationSchema = yup.object({
  patientId: yup.number().required(t('shiftBatchCreateForm.fieldRequired')),
  startDate: yup.string().required(t('shiftBatchCreateForm.fieldRequired')),
  caregiversIds: yup.array().required(t('shiftBatchCreateForm.fieldRequired')),
  caregiversShifts: yup.array().required(t('shiftBatchCreateForm.fieldRequired')),
  caregiversPricing: yup.array().of(caregiverPricingSchema),
});

const groupFilter = useGroupFilterStore();
const patientPickerGroupIds = computed(() => groupFilter.groupIds ?? []);

const { data: patients, isFetching: isFetchingPatients } = useQuery({
  queryKey: ['patientsForFilters', patientPickerGroupIds],
  queryFn: () => patientsApi.indexForFilters(patientPickerGroupIds.value),
  placeholderData: keepPreviousData,
});

const initialValues = {
  patientId: props.patient?.id,
  startDate: undefined,
  caregiversIds: [],
  caregiversShifts: [],
  caregiversPricing: [],
  startTime: undefined,
  endTime: undefined,
};

const { handleSubmit, values, errors, defineField, setFieldValue } = useForm<ShiftBatchCreateForm>(
  { validationSchema, initialValues },
);

const [patientId, patientIdAttrs] = defineField('patientId');
const [startDate, startDateAttrs] = defineField('startDate');
const [caregiversIds, caregiversIdsAttrs] = defineField('caregiversIds');
const [caregiversShifts] = defineField('caregiversShifts');
const [caregiversPricing] = defineField('caregiversPricing');
const [startTime] = defineField('startTime');
const [endTime] = defineField('endTime');

const groupsEnabled = computed(() => !!currentUser?.value?.companyGroupsEnabled);
const caregiverPickerGroupIds = ref<number[]>([]);

const { data: caregivers, isFetching: isFetchingCaregivers } = useQuery({
  queryKey: ['caregiversForFilters', caregiverPickerGroupIds],
  queryFn: () => caregiversApi.indexForFilters(caregiverPickerGroupIds.value),
  placeholderData: keepPreviousData,
});

watch(
  [patientId, () => groupFilter.groupIds, patients, groupsEnabled],
  () => {
    caregiverPickerGroupIds.value = groupsEnabled.value ?
      resolveMemberGroupIds(patientId.value, patients.value, groupFilter.groupIds ?? []) :
      [];
  },
  { immediate: true },
);

watch(caregivers, (newList, oldList) => {
  if (oldList === undefined) return;
  if (!newList || !caregiversIds.value || caregiversIds.value.length === 0) return;
  const validIds = new Set(newList.map((c: CaregiverFilter) => c.id));
  const pruned = caregiversIds.value.filter(id => validIds.has(id));
  if (pruned.length !== caregiversIds.value.length) {
    setFieldValue('caregiversIds', pruned);
  }
});

watch(patients, (newList, oldList) => {
  if (oldList === undefined) return;
  if (!newList || !patientId.value) return;
  if (!newList.some((p: PatientFilter) => p.id === patientId.value)) {
    setFieldValue('patientId', undefined as never);
  }
});

// One grid entry per selected caregiver, paired by index with caregiversIds (the
// backend reads them by position) and following its caregiver, not its slot: a
// deselected caregiver's days go with it instead of to the next caregiver, and
// never past the selection where they would be sent with no caregiver at all.
watch(caregiversIds, (ids, previousIds) => {
  const byCaregiver = new Map((previousIds ?? []).map((id, index) => [id, caregiversShifts.value[index]]));
  caregiversShifts.value = (ids ?? []).map(id => byCaregiver.get(id) ?? []);
});

function updateCaregiverShifts(index: number, newShifts: Array<CaregiverShift>) {
  caregiversShifts.value.splice(index, 1, newShifts);
}

const currentPatient = computed(
  () => patients?.value?.find((patient: PatientFilter) => patient.id === patientId.value),
);

const currentPatientIdRef = computed(() => currentPatient.value?.id);
const selectedCaregiverIds = computed<number[]>(() => caregiversIds.value ?? []);

// With groups enabled a shift needs a group shared by patient and caregiver, so a
// patient outside every group can be assigned nobody: the picker offers nobody
// instead of the global filter's caregivers, who would all fail on save.
const patientWithoutGroups = computed(() => groupsEnabled.value && memberWithoutGroups(currentPatient.value));
const caregiverOptions = computed(() => (patientWithoutGroups.value ? [] : caregivers.value));

watch(patientWithoutGroups, (blocked) => {
  if (blocked && (caregiversIds.value?.length ?? 0) > 0) setFieldValue('caregiversIds', []);
});

const {
  pricingByCaregiverId,
  loadingByCaregiverId,
  errorByCaregiverId,
  isAnyLoading: isPricingLoading,
  hasAnyError: hasPricingError,
} = usePricingStructures(currentPatientIdRef, selectedCaregiverIds, canReadFinancialInfo);

function getCaregiver(caregiverId: number): CaregiverFilter | undefined {
  return caregivers.value?.find((caregiver: CaregiverFilter) => caregiver.id === caregiverId);
}

function isSalaried(caregiverId: number): boolean {
  return getCaregiver(caregiverId)?.contractedWithSalary ?? false;
}

function buildPricingEntry(caregiverId: number, pricing: TransformedPricingStructure): CaregiverPricing {
  return {
    caregiverId,
    caregiverWeekShiftCost: pricing.weekCost,
    caregiverWeekendShiftCost: pricing.weekendCost,
    weekShiftPrice: pricing.weekPrice,
    weekendShiftPrice: pricing.weekendPrice,
    caregiverWeekShiftCostPerHour: pricing.weekCostPerHour,
    caregiverWeekendShiftCostPerHour: pricing.weekendCostPerHour,
    weekShiftPricePerHour: pricing.weekPricePerHour,
    weekendShiftPricePerHour: pricing.weekendPricePerHour,
    withTaxBill: pricing.withTaxBill,
  };
}

// Patient each caregiver's entry was last pre-filled for. An entry is
// overwritten with the caregiver's own pricing only when that pricing resolves
// for a patient it has not been applied for yet, so user edits survive other
// caregivers' pricing arriving later, and a deselected caregiver starts fresh.
const prefilledPatientIdByCaregiverId = new Map<number, number | undefined>();

function syncCaregiversPricing() {
  const selectedIds = selectedCaregiverIds.value;
  const existingEntries = new Map(
    (caregiversPricing.value ?? []).map((entry: CaregiverPricing) => [entry.caregiverId, entry]),
  );
  prefilledPatientIdByCaregiverId.forEach((_patientId, caregiverId) => {
    if (!selectedIds.includes(caregiverId)) prefilledPatientIdByCaregiverId.delete(caregiverId);
  });

  caregiversPricing.value = selectedIds.map((caregiverId) => {
    const pricing = pricingByCaregiverId.value[caregiverId];
    if (pricing && prefilledPatientIdByCaregiverId.get(caregiverId) !== patientId.value) {
      prefilledPatientIdByCaregiverId.set(caregiverId, patientId.value);

      return buildPricingEntry(caregiverId, pricing);
    }

    return existingEntries.get(caregiverId) ?? buildPricingEntry(caregiverId, EMPTY_PRICING);
  });
}

watch([selectedCaregiverIds, pricingByCaregiverId], syncCaregiversPricing, { immediate: true });

function pricingEntryError(index: number, field: keyof CaregiverPricing): string | undefined {
  return (errors.value as Record<string, string | undefined>)[`caregiversPricing[${index}].${field}`];
}

interface ComplianceCheck {
  caregiverDisplayName: string;
  complianceCheck: {
    firstRuleText: string;
    secondRuleText: string;
    thirdRuleText: string;
  };
}

const complianceChecks = ref<ComplianceCheck[]>([]);
const showComplianceModal = ref(false);
const conflictDetector = ref<InstanceType<typeof ShiftConflictDetector>>();
const redirectToShiftsTimeoutMs = 1500;
function redirectToShifts() {
  setTimeout(() => {
    window.location.href = `/shifts?patient_id=${patientId.value}`;
  }, redirectToShiftsTimeoutMs);
}

function getAnalyticsEventType(): string {
  return props.source === 'patient_profile' ?
    AnalyticsEvents.shifts.createdManualPatientProfile :
    AnalyticsEvents.shifts.createdManualCalendar;
}

function extractShiftsCount(data: unknown): number {
  if (Array.isArray(data)) return data.length;
  const obj = data as { shifts?: unknown[] };
  return obj?.shifts?.length ?? 0;
}

const submitButtonDisabled = ref(false);

const {
  mutate: createShifts, error: createError, isError: isCreateError, isIdle, isPending, isSuccess,
} = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => shiftsApi.create({ ...values }),
    // The form stays on screen after a failed create (see the v-form v-show) so the
    // user can act on the message, e.g. pick another caregiver, and submit again.
    onError: () => {
      submitButtonDisabled.value = false;
    },
    onSuccess: (data) => {
      trackShiftEvent(getAnalyticsEventType(), {
        patientId: patientId.value,
        caregiversCount: caregiversIds.value?.length,
        shiftsCount: extractShiftsCount(data),
      });

      const responseData = data as { meta?: { complianceChecks?: ComplianceCheck[] } };
      if (responseData?.meta?.complianceChecks && responseData.meta.complianceChecks.length > 0) {
        complianceChecks.value = responseData.meta.complianceChecks;
        showComplianceModal.value = true;
      } else {
        redirectToShifts();
      }
    },
  },
);

// Validation messages of the 400 (e.g. the caregiver shares no group with the
// patient); any other failure gets the generic message.
const createErrorMessage = computed(
  () => requestErrorMessage(createError.value, t('userSession.defaultError')),
);

function handleConflictCancel() {
  submitButtonDisabled.value = false;
}

watch(isSuccess, async () => {
  if (isSuccess.value === true && !showComplianceModal.value) {
    redirectToShifts();
  }
});

function closeComplianceModal() {
  showComplianceModal.value = false;
  redirectToShifts();
}

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  conflictDetector.value?.checkConflicts({ ...values });
});

const formattedStartDate = computed(() => startDate.value.replace(/-/g, '/'));

function getCaregiverName(caregiverId: number) {
  if (caregivers.value === undefined) return '';
  const caregiver = caregivers.value.find(c => c.id === caregiverId);

  return caregiver ? caregiver.fullName : '';
}

const steps = computed(() => {
  const stepsArray = [t('shiftBatchCreateForm.stepOneTitle'), t('shiftBatchCreateForm.stepTwoTitle')];
  caregiversIds.value?.forEach((caregiverId: number) => {
    stepsArray.push(`Turnos ${getCaregiverName(caregiverId)}`);
  });
  if (canReadFinancialInfo.value) {
    stepsArray.push(t('shiftBatchCreateForm.stepThreeTitle'));
  }

  return stepsArray;
});

function emptyBlockedShift(day: string): CaregiverShift {
  return { day, dayShift: false, nightShift: false, customShift: false, customShifts: [] };
}

function mergeBlockedShift(blocked: CaregiverShift, shift: CaregiverShift): CaregiverShift {
  return {
    day: blocked.day,
    dayShift: blocked.dayShift || shift.dayShift,
    nightShift: blocked.nightShift || shift.nightShift,
    customShift: blocked.customShift || shift.customShift,
    customShifts: [...(blocked.customShifts ?? []), ...(shift.customShifts ?? [])],
  };
}

// Slots the other caregivers of the batch already took, merged by date (a grid
// extended by a week never shifts another caregiver's days) and carrying every
// custom range taken that day.
function getBlockedShifts(caregiverIndex: number): CaregiverShift[] {
  const blockedByDay = new Map<string, CaregiverShift>();
  caregiversShifts.value.forEach((caregiverShifts, currentIndex) => {
    if (caregiverIndex === currentIndex) return;

    caregiverShifts.forEach((shift) => {
      const blocked = blockedByDay.get(shift.day) ?? emptyBlockedShift(shift.day);
      blockedByDay.set(shift.day, mergeBlockedShift(blocked, shift));
    });
  });

  return [...blockedByDay.values()];
}

const shiftType = ref('dayAndNight');
const showDayAndNightShifts = ref(true);
const showTwentyFourHoursShifts = ref(false);
const showCustomShifts = ref(false);
const showShifts = computed(() => [
  showDayAndNightShifts.value,
  showDayAndNightShifts.value,
  showTwentyFourHoursShifts.value,
  showCustomShifts.value,
]);

// A "Personalizados" batch carries a list of time ranges shared by every
// caregiver; each grid shows one row per range and marks, per day, which ones
// that caregiver takes. The list is UI state only: the grids fan it out per day
// into caregiversShifts, which is what the backend reads.
const MAX_CUSTOM_RANGES = 10;
const DEFAULT_CUSTOM_RANGE_START = '08:00';
const DEFAULT_CUSTOM_RANGE_MINUTES = 60;

// Each row carries a client-side key so removing a range in the middle of the
// list does not migrate the remaining inputs' state to another range.
interface CustomRangeRow extends CustomShiftRange {
  key: number;
}

let customRangeKeySequence = 0;
const customRanges = ref<CustomRangeRow[]>([]);

// A new range lasts the default hour and starts where the previous one ends,
// so consecutive ranges never collide; the first one opens the working day.
function addCustomRange() {
  if (customRanges.value.length >= MAX_CUSTOM_RANGES) return;
  const previous = customRanges.value[customRanges.value.length - 1];
  const rangeStart = previous && previous.endTime !== '' ? previous.endTime : DEFAULT_CUSTOM_RANGE_START;
  customRanges.value.push({
    key: customRangeKeySequence++,
    startTime: rangeStart,
    endTime: addMinutesToTime(rangeStart, DEFAULT_CUSTOM_RANGE_MINUTES),
  });
}

// The end time follows the start: a range keeps its duration when its start
// moves, and a range without an end yet gets the default hour. An end typed
// before any start is the user's and stays.
function updateCustomRangeStart(index: number, newStart: string) {
  const range = customRanges.value[index];
  if (!range) return;
  range.endTime = endTimeFollowingStart(range.startTime, newStart, range.endTime, DEFAULT_CUSTOM_RANGE_MINUTES);
  range.startTime = newStart;
}

function removeCustomRange(index: number) {
  if (customRanges.value.length <= 1) return;
  customRanges.value.splice(index, 1);
}

function isCustomRangeFilled(range: CustomShiftRange): boolean {
  return range.startTime !== '' && range.endTime !== '';
}

function sameCustomRange(a: CustomShiftRange, b: CustomShiftRange): boolean {
  return a.startTime === b.startTime && a.endTime === b.endTime;
}

// A range identical to an earlier one is reported on the later row only.
function isDuplicateCustomRange(index: number): boolean {
  const range = customRanges.value[index];
  if (!range || !isCustomRangeFilled(range)) return false;

  return customRanges.value.slice(0, index).some(earlier => sameCustomRange(earlier, range));
}

function customRangeError(index: number): string | undefined {
  return isDuplicateCustomRange(index) ? t('shiftBatchCreateForm.duplicateCustomRange') : undefined;
}

const areCustomRangesValid = computed(() =>
  customRanges.value.length > 0 &&
  customRanges.value.every(isCustomRangeFilled) &&
  !customRanges.value.some((_range, index) => isDuplicateCustomRange(index)),
);

// What the day grids build their custom rows from: complete ranges only, and
// none at all unless the batch is of the custom type.
const activeCustomRanges = computed<CustomShiftRange[]>(() => {
  if (!showCustomShifts.value) return [];

  return customRanges.value
    .filter(isCustomRangeFilled)
    .map(range => ({ startTime: range.startTime, endTime: range.endTime }));
});

watch(shiftType, () => {
  showDayAndNightShifts.value = shiftType.value === 'dayAndNight';
  showTwentyFourHoursShifts.value = shiftType.value === '24';
  showCustomShifts.value = shiftType.value === 'custom';

  if (shiftType.value === '24') {
    endTime.value = startTime.value;
  }
  if (shiftType.value === 'custom' && customRanges.value.length === 0) {
    addCustomRange();
  }
});

watch(startTime, () => {
  if (shiftType.value === '24') {
    endTime.value = startTime.value;
  }
});

function isFilled(value: boolean | string | number | null | undefined) {
  return (value !== '' && value !== null && value !== undefined);
}
function isTimeFilled(value: string | null | undefined) {
  return (value !== '' && value !== null && value !== undefined);
}
const currentStep = ref(0);
const isPatientAndCaregiversStepCompleted = computed(() =>
  isFilled(patientId.value) &&
  caregiversIds.value.length > 0 &&
  isFilled(startDate.value),
);
const isShiftsTypeStepCompleted = computed(() => {
  if (showCustomShifts.value) return areCustomRangesValid.value;
  if (showTwentyFourHoursShifts.value) return isTimeFilled(startTime.value) && isTimeFilled(endTime.value);

  return true;
});
// A salaried caregiver's cost inputs and tax-bill choice are hidden (the
// backend stores cost 0), so only its prices are required.
function isPricingEntryCompleted(entry: CaregiverPricing): boolean {
  const priceFields = chargesAndPaysPerHours.value ?
    [entry.weekShiftPricePerHour, entry.weekendShiftPricePerHour] :
    [entry.weekShiftPrice, entry.weekendShiftPrice];
  if (isSalaried(entry.caregiverId)) return priceFields.every(isFilled);

  const costFields = chargesAndPaysPerHours.value ?
    [entry.caregiverWeekShiftCostPerHour, entry.caregiverWeekendShiftCostPerHour] :
    [entry.caregiverWeekShiftCost, entry.caregiverWeekendShiftCost];

  return [...priceFields, ...costFields, entry.withTaxBill].every(isFilled);
}
const isFinancialInformationStepCompleted = computed(
  () => (caregiversPricing.value ?? []).every(isPricingEntryCompleted),
);

const stepValidation: Record<number, () => boolean> = {
  0: () => isPatientAndCaregiversStepCompleted.value,
  1: () => isShiftsTypeStepCompleted.value,
};

const canGoToNextStep = computed(() => {
  const lastStep = steps.value.length - 1;
  if (currentStep.value === lastStep) return isFinancialInformationStepCompleted.value;
  if (currentStep.value === 0 && isPricingLoading.value) return false;

  const validator = stepValidation[currentStep.value];

  return validator ? validator() : true;
});

const formattedStartTime = computed(() => startTime.value ?? '');
const formattedEndTime = computed(() => endTime.value ?? '');
</script>

<template>
  <v-card
    v-if="hasPermission('shifts', 'create')"
    class="v-col-12 v-col-md-10 pa-4"
    flat
  >
    <v-card-title class="mb-4">
      {{ t('shiftBatchCreateForm.title') }}
    </v-card-title>
    <shifts-info-banner class="mx-2" />
    <!-- v-show, not v-if: the days grids keep their own checked state, so the form
         must survive the pending card to come back intact after a failed create. -->
    <v-form
      v-show="isIdle || isCreateError"
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-stepper v-model="currentStep">
        <template #default="{ prev, next }">
          <v-stepper-header>
            <template
              v-for="(step, index) in steps"
              :key="`${step}-step`"
            >
              <v-stepper-item
                :step="step"
                :value="index"
                icon="mdi-form-select"
                :title="steps[index]"
                :complete="index < currentStep"
                :color="index <= currentStep ? 'primary' : 'gray'"
              />

              <v-divider
                v-if="index !== steps.length - 1"
                :key="index"
              />
            </template>
          </v-stepper-header>

          <v-stepper-window>
            <v-stepper-window-item>
              <v-card flat>
                <v-card-subtitle class="mb-6 text-black text-subtitle-1">
                  {{ t('shiftBatchCreateForm.stepOneSubtitle') }}
                </v-card-subtitle>
                <v-autocomplete
                  v-model="patientId"
                  v-bind="patientIdAttrs"
                  variant="outlined"
                  item-title="fullName"
                  item-value="id"
                  :label="t('shift.patient')"
                  :error-messages="errors.patientId"
                  :items="patients"
                  :loading="isFetchingPatients"
                  :custom-filter="nameFilter"
                />
                <v-alert
                  v-if="patientWithoutGroups"
                  class="mb-4 patient-without-groups-alert"
                  type="warning"
                  variant="tonal"
                >
                  {{ t('shiftBatchCreateForm.patientWithoutGroups') }}
                </v-alert>
                <v-autocomplete
                  v-model="caregiversIds"
                  v-bind="caregiversIdsAttrs"
                  variant="outlined"
                  item-title="fullName"
                  item-value="id"
                  :label="t('shiftBatchCreateForm.caregivers')"
                  :error-messages="errors.caregiversIds"
                  :items="caregiverOptions"
                  :loading="isFetchingCaregivers"
                  :disabled="patientWithoutGroups"
                  :custom-filter="nameFilter"
                  chips
                  multiple
                />
                <v-text-field
                  v-model="startDate"
                  type="date"
                  v-bind="startDateAttrs"
                  variant="outlined"
                  hint="A partir de esta fecha asignarás los turnos."
                  persistent-hint
                  :label="t('shift.startDate')"
                  :error-messages="errors.startDate"
                />
              </v-card>
            </v-stepper-window-item>
            <v-stepper-window-item>
              <v-card flat>
                <v-card-subtitle class="mb-6 text-black text-subtitle-1">
                  {{ t('shiftBatchCreateForm.stepTwoSubtitle') }}
                </v-card-subtitle>
                <v-select
                  v-model="shiftType"
                  :items="[
                    { title: 'Turnos Día (8:00 a 20:00) y Noche (20:00 a 8:00)', value: 'dayAndNight' },
                    { title: 'Turnos 24 horas', value: '24' },
                    { title: 'Turnos Personalizados', value: 'custom' }
                  ]"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  label="Tipo de Turno"
                  class="mt-8 ml-4 w-50"
                />
                <v-col
                  v-if="showTwentyFourHoursShifts"
                  class="mb-4 d-flex flex-column"
                >
                  <p class="ml-6 mb-2 text-caption text-grey">
                    {{ t('shiftForm.shiftType24Info') }}
                  </p>
                  <time-input
                    v-model="startTime"
                    class="w-50 pa-4"
                    label="Hora Inicio"
                    :error-messages="errors.startTime"
                  />
                </v-col>
                <div
                  v-if="showCustomShifts"
                  class="mb-4 px-4 custom-ranges"
                >
                  <p class="ml-2 mb-4 text-caption text-grey">
                    {{ t('shiftBatchCreateForm.customRangesInfo', { max: MAX_CUSTOM_RANGES }) }}
                  </p>
                  <div
                    v-for="(range, index) in customRanges"
                    :key="range.key"
                    class="d-flex flex-wrap align-start ga-4 mb-2 custom-range"
                  >
                    <time-input
                      :model-value="range.startTime"
                      class="custom-range-time"
                      label="Hora Inicio"
                      :error-messages="customRangeError(index)"
                      @update:model-value="updateCustomRangeStart(index, $event)"
                    />
                    <time-input
                      v-model="range.endTime"
                      class="custom-range-time"
                      label="Hora Término"
                    />
                    <v-btn
                      icon="mdi-close"
                      variant="text"
                      size="small"
                      class="custom-range-remove"
                      :aria-label="t('shiftBatchCreateForm.removeCustomRange')"
                      :disabled="customRanges.length === 1"
                      @click="removeCustomRange(index)"
                    />
                  </div>
                  <v-btn
                    variant="text"
                    color="primary"
                    prepend-icon="mdi-plus"
                    class="custom-range-add"
                    :disabled="customRanges.length >= MAX_CUSTOM_RANGES"
                    @click="addCustomRange"
                  >
                    {{ t('shiftBatchCreateForm.addCustomRange') }}
                  </v-btn>
                </div>
              </v-card>
            </v-stepper-window-item>
            <v-stepper-window-item
              v-for="(caregiverId, index) in caregiversIds"
              :key="`caregiver-step-${caregiverId}`"
            >
              <v-card
                :title="`Turnos ${getCaregiverName(caregiverId)}`"
                flat
              >
                <v-card-subtitle
                  v-if="shiftType === '24'"
                  class="mb-6 text-black text-caption"
                >
                  {{ t('shiftBatchCreateForm.twentyFourHoursInfo', {
                    startTime: formattedStartTime,
                    endTime: formattedEndTime
                  }) }}
                </v-card-subtitle>
                <shift-days-selection-form
                  :first-day="formattedStartDate"
                  :caregiver-id="caregiverId"
                  :blocked-shifts="getBlockedShifts(index)"
                  :show-shifts="showShifts"
                  :custom-ranges="activeCustomRanges"
                  class="mb-6"
                  :value="caregiversShifts[index]"
                  @input="updateCaregiverShifts(index, $event)"
                />
              </v-card>
            </v-stepper-window-item>
            <v-stepper-window-item v-if="canReadFinancialInfo">
              <v-card flat>
                <v-card-subtitle class="mb-6 text-black text-subtitle-1">
                  {{ t('shiftBatchCreateForm.stepThreeSubtitle') }}
                </v-card-subtitle>
                <v-card
                  v-for="(entry, index) in caregiversPricing"
                  :key="`pricing-${entry.caregiverId}`"
                  class="mx-2 mb-6 caregiver-pricing-block"
                  elevation="2"
                >
                  <v-card-title class="d-flex align-center flex-wrap ga-2 text-h6 pt-4">
                    <span class="caregiver-pricing-title">{{ getCaregiverName(entry.caregiverId) }}</span>
                    <v-chip
                      v-if="getCaregiver(entry.caregiverId)?.humanCareer"
                      size="small"
                      variant="tonal"
                      :color="getCareerColor(getCaregiver(entry.caregiverId)?.career ?? '')"
                      class="caregiver-pricing-career"
                    >
                      {{ getCaregiver(entry.caregiverId)?.humanCareer }}
                    </v-chip>
                  </v-card-title>
                  <v-alert
                    v-if="errorByCaregiverId[entry.caregiverId]"
                    type="error"
                    variant="tonal"
                    class="mx-4 mb-4 caregiver-pricing-error"
                  >
                    {{ t('shiftBatchCreateForm.pricingLoadError') }}
                  </v-alert>
                  <v-row
                    v-else
                    class="ma-0"
                  >
                    <v-col
                      cols="12"
                      md="7"
                      class="pa-0"
                    >
                      <v-card flat>
                        <v-card-title class="text-subtitle-1 font-weight-medium">
                          {{ t('shiftBatchCreateForm.priceTitle') }}
                        </v-card-title>
                        <v-row class="mx-4 my-2">
                          <template v-if="loadingByCaregiverId[entry.caregiverId]">
                            <v-skeleton-loader type="text" />
                            <v-skeleton-loader
                              class="ml-4"
                              type="text"
                            />
                          </template>
                          <template v-else-if="chargesAndPaysPerHours">
                            <price-input
                              v-model="entry.weekShiftPricePerHour"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.weekShiftPricePerHour')"
                              :error-messages="pricingEntryError(index, 'weekShiftPricePerHour')"
                            />
                            <price-input
                              v-model="entry.weekendShiftPricePerHour"
                              class="ml-4"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.weekendShiftPricePerHour')"
                              :error-messages="pricingEntryError(index, 'weekendShiftPricePerHour')"
                            />
                          </template>
                          <template v-else>
                            <price-input
                              v-model="entry.weekShiftPrice"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.weekShiftPrice')"
                              :error-messages="pricingEntryError(index, 'weekShiftPrice')"
                            />
                            <price-input
                              v-model="entry.weekendShiftPrice"
                              class="ml-4"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.weekendShiftPrice')"
                              :error-messages="pricingEntryError(index, 'weekendShiftPrice')"
                            />
                          </template>
                        </v-row>
                      </v-card>
                      <v-alert
                        v-if="isSalaried(entry.caregiverId)"
                        type="info"
                        variant="tonal"
                        class="mx-4 mb-4 caregiver-salary-alert"
                      >
                        {{ t('shiftBatchCreateForm.caregiverHasSalaryInfo') }}
                      </v-alert>
                      <v-card
                        v-else
                        class="mt-n5"
                        flat
                      >
                        <v-card-title class="text-subtitle-1 font-weight-medium">
                          {{ t('shiftBatchCreateForm.caregiverCostTitle') }}
                        </v-card-title>
                        <v-row class="mx-4 my-2">
                          <template v-if="loadingByCaregiverId[entry.caregiverId]">
                            <v-skeleton-loader type="text" />
                            <v-skeleton-loader
                              class="ml-4"
                              type="text"
                            />
                          </template>
                          <template v-else-if="chargesAndPaysPerHours">
                            <price-input
                              v-model="entry.caregiverWeekShiftCostPerHour"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.caregiverWeekShiftCostPerHour')"
                              :error-messages="pricingEntryError(index, 'caregiverWeekShiftCostPerHour')"
                            />
                            <price-input
                              v-model="entry.caregiverWeekendShiftCostPerHour"
                              class="ml-4"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.caregiverWeekendShiftCostPerHour')"
                              :error-messages="pricingEntryError(index, 'caregiverWeekendShiftCostPerHour')"
                            />
                          </template>
                          <template v-else>
                            <price-input
                              v-model="entry.caregiverWeekShiftCost"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.caregiverWeekShiftCost')"
                              :error-messages="pricingEntryError(index, 'caregiverWeekShiftCost')"
                            />
                            <price-input
                              v-model="entry.caregiverWeekendShiftCost"
                              class="ml-4"
                              max-width="260"
                              :label="t('shiftBatchCreateForm.caregiverWeekendShiftCost')"
                              :error-messages="pricingEntryError(index, 'caregiverWeekendShiftCost')"
                            />
                          </template>
                        </v-row>
                      </v-card>
                    </v-col>
                    <v-col
                      v-if="!isSalaried(entry.caregiverId)"
                      cols="12"
                      md="5"
                      class="d-flex flex-column justify-end position-relative px-4 pb-7"
                    >
                      <v-skeleton-loader
                        v-if="loadingByCaregiverId[entry.caregiverId]"
                        type="text"
                      />
                      <template v-else>
                        <span class="d-block text-body-1 text-medium-emphasis mb-1">
                          {{ t('shiftBatchCreateForm.withTaxBillTitle') }}
                        </span>
                        <v-radio-group
                          v-model="entry.withTaxBill"
                          :aria-label="t('shiftBatchCreateForm.withTaxBillTitle')"
                          :error-messages="pricingEntryError(index, 'withTaxBill')"
                          class="flex-grow-0"
                          hide-details="auto"
                          inline
                        >
                          <v-radio
                            :label="t('shiftBatchCreateForm.yes')"
                            :value="true"
                          />
                          <v-radio
                            :label="t('shiftBatchCreateForm.no')"
                            :value="false"
                          />
                        </v-radio-group>
                      </template>
                    </v-col>
                  </v-row>
                </v-card>
              </v-card>
            </v-stepper-window-item>
          </v-stepper-window>

          <v-stepper-actions
            :class="currentStep === 0 ? 'justify-end' : 'justify-between'"
            :disabled="canGoToNextStep ? false : 'next'"
          >
            <template #prev>
              <v-btn
                v-if="currentStep !== 0"
                @click="prev"
              >
                {{ t('shiftBatchCreateForm.previous') }}
              </v-btn>
            </template>
            <template #next>
              <v-btn
                v-if="currentStep !== steps.length - 1"
                class="align-self-end"
                color="primary"
                @click="next"
              >
                {{ t('shiftBatchCreateForm.next') }}
              </v-btn>
              <v-btn
                v-else
                variant="flat"
                color="primary"
                type="submit"
                :disabled="submitButtonDisabled || isPricingLoading || hasPricingError"
              >
                {{ t('shiftBatchCreateForm.submit') }}
              </v-btn>
            </template>
          </v-stepper-actions>
        </template>
      </v-stepper>
    </v-form>
    <v-card
      v-if="isPending"
      class="pa-16"
    >
      <v-col>
        <v-row class="justify-center d-flex">
          <v-progress-circular
            class="mx-auto"
            indeterminate
            color="primary"
          />
        </v-row>
        <v-row class="justify-center mt-8 d-flex">
          <p>Estamos creando los turnos, esto puede tomar unos segundos...</p>
        </v-row>
      </v-col>
    </v-card>
    <v-card
      v-if="isSuccess"
      class="pa-16"
    >
      <v-col>
        <v-row class="justify-center d-flex">
          <v-icon
            icon="mdi-check-circle"
            color="primary"
            size="50"
          />
        </v-row>
        <v-row class="justify-center mt-8 d-flex">
          <p>Turnos creados exitosamente!</p>
        </v-row>
      </v-col>
    </v-card>
    <v-alert
      v-if="isCreateError"
      class="mt-4 rounded create-error-alert"
      type="error"
    >
      {{ createErrorMessage }}
    </v-alert>
    <compliance-check-modal
      v-model="showComplianceModal"
      :compliance-checks="complianceChecks"
      @update:model-value="closeComplianceModal"
    />

    <shift-conflict-detector
      ref="conflictDetector"
      @confirm="createShifts"
      @cancel="handleConflictCancel"
    />
  </v-card>
</template>

<style scoped>
.custom-range-time {
  flex: 1 1 200px;
  max-width: 260px;
}
</style>
